/**
 * @fileoverview Modal dialog for exporting data to clipboard or file in multiple formats.
 * @module ui/ExportDialog
 */

import { BaseDialog } from './BaseDialog.js';
import { escapeHtml, getOwnValue } from '../utils/jrfuncs.js';


/**
 * @typedef {Object} FormatConverter
 * @property {string} label - Display label for the format in the dropdown
 * @property {string} fileExtension - File extension including the dot (e.g., '.json')
 * @property {string} [mimeType='text/plain'] - MIME type for the file download
 * @property {Function} converter - Function that takes contentJson and returns formatted string
 */

/**
 * Modal dialog helper class for exporting data in multiple formats.
 * Provides format selection, read-only preview, copy to clipboard, and save to file functionality.
 * @class
 * @extends BaseDialog
 */
export class ExportDialog extends BaseDialog {
    /**
     * Creates a new ExportDialog instance.
     * @param {Object} options - Configuration options
     * @param {*} options.contentJson - The data to export (will be passed to format converters)
     * @param {Object<string, FormatConverter>} options.formatConverters - Dictionary of format converters
     * @param {string} [options.defaultFormat] - Key of the default format to select (defaults to first format)
     * @param {string} [options.title='Export Data'] - Modal title
     * @param {string} [options.infoText=null] - Optional HTML text to display at top of dialog
     * @param {string} [options.defaultFilename='export'] - Default filename (without extension)
     * @param {string} [options.closeButtonText='Close'] - Text for the close button
     */
    constructor(options = {}) {
        super({ idPrefix: 'exportDialog' });

        if (!options.formatConverters || Object.keys(options.formatConverters).length === 0) {
            throw new Error('ExportDialog requires at least one format converter');
        }

        // the default format is often the user's remembered last choice, read from storage or a settings
        // backup: a key that names no converter (another version's, a renamed format, "constructor") falls
        // back to the first format -- a bare lookup made every export and clipboard button do nothing
        const formatKeys = Object.keys(options.formatConverters);
        const defaultFormat = getOwnValue(options.formatConverters, options.defaultFormat) ? options.defaultFormat : formatKeys[0];

        this.options = {
            contentJson: options.contentJson,
            formatConverters: options.formatConverters,
            defaultFormat: defaultFormat,
            title: options.title || 'Export Data',
            infoText: options.infoText || null,
            defaultFilename: options.defaultFilename || 'export',
            closeButtonText: options.closeButtonText || 'Close'
        };

        this.currentFormat = defaultFormat;
        this.currentContent = '';
    }

    /**
     * Called before the modal is shown.
     * Generates initial content for default format.
     * @override
     */
    onBeforeShow() {
        this.updateFormatDisplay(this.currentFormat);
        this.lastFormat = this.currentFormat;
    }

    /**
     * Returns the HTML for the export modal.
     * Uses buildModalHtml template from BaseDialog.
     * @returns {string} Modal HTML string
     * @override
     */
    getModalHtml() {
        const currentConverter = getOwnValue(this.options.formatConverters, this.currentFormat);

        const bodyHtml = `
            <div class="export-dialog-content">
                <div class="export-dialog-format-section">
                    <label for="${this.modalId}_formatSelect" class="form-label">Export format:</label>
                    <select class="form-select" id="${this.modalId}_formatSelect">
                        ${this._buildFormatOptions()}
                    </select>
                    <label for="${this.modalId}_textarea" class="form-label">
                        Export preview is shown below.
                    </label>
                </div>
                <div>
                    <textarea
                        class="form-control export-dialog-textarea"
                        id="${this.modalId}_textarea"
                        rows="12"
                        readonly
                    ></textarea>
                </div>
                <div class="export-dialog-actions">
                    <div class="export-dialog-filename">
                        <label for="${this.modalId}_filename" class="form-label">Filename:</label>
                        <div class="input-group">
                            <input
                                type="text"
                                class="form-control"
                                id="${this.modalId}_filename"
                                value="${escapeHtml(this.options.defaultFilename)}"
                            >
                            <span class="input-group-text" id="${this.modalId}_fileExt">${currentConverter.fileExtension}</span>
                        </div>
                    </div>
                    <div class="export-dialog-action-buttons">
                        <button type="button" class="btn btn-outline-primary" id="${this.modalId}_saveBtn">
                            <i class="bi bi-download"></i> Save to File
                        </button>
                        <button type="button" class="btn btn-outline-primary" id="${this.modalId}_copyBtn">
                            <i class="bi bi-clipboard"></i> Copy to Clipboard
                        </button>
                    </div>
                </div>
            </div>
        `;

        const footerHtml = `
            <div class="export-dialog-status" id="${this.modalId}_status"></div>
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                ${this.options.closeButtonText}
            </button>
        `;

        return this.buildModalHtml({
            title: this.options.title,
            icon: 'bi-box-arrow-up',
            bodyHtml,
            footerHtml,
            infoText: this.options.infoText
        });
    }

    /**
     * Builds format options HTML for the dropdown.
     * @returns {string} HTML string of option elements
     * @private
     */
    _buildFormatOptions() {
        return Object.entries(this.options.formatConverters)
            .map(([key, format]) => {
                const selected = key === this.currentFormat ? 'selected' : '';
                return `<option value="${key}" ${selected}>${format.label}</option>`;
            })
            .join('');
    }

    /**
     * Updates the preview textarea and file extension based on selected format.
     * @param {string} formatKey - The key of the format to display
     * @private
     */
    updateFormatDisplay(formatKey) {
        const converter = getOwnValue(this.options.formatConverters, formatKey);
        if (!converter) {
            console.error(`ERROR: Unknown format: ${formatKey}`);
            return;
        }

        this.currentFormat = formatKey;

        // Convert content using the format's converter function
        try {
            this.currentContent = converter.converter(this.options.contentJson);
        } catch (error) {
            console.error(`ERROR: Failed converting to format ${formatKey}:`, error);
            this.currentContent = `Error converting data: ${error.message}`;
        }

        // Update textarea
        const textarea = this.modalElement.querySelector(`#${this.modalId}_textarea`);
        if (textarea) {
            textarea.value = this.currentContent;
        }

        // Update file extension display
        const fileExtSpan = this.modalElement.querySelector(`#${this.modalId}_fileExt`);
        if (fileExtSpan) {
            fileExtSpan.textContent = converter.fileExtension;
        }
    }

    /**
     * Gets the current format's converter configuration.
     * @returns {FormatConverter} The current format converter
     * @private
     */
    getCurrentConverter() {
        return getOwnValue(this.options.formatConverters, this.currentFormat);
    }

    /**
     * Sets up event listeners for modal interactions.
     * @override
     */
    setupEventListeners() {
        const formatSelect = this.getElement('_formatSelect');
        const copyBtn = this.getElement('_copyBtn');
        const saveBtn = this.getElement('_saveBtn');
        const statusDiv = this.getElement('_status');

        // Format selection change
        formatSelect.addEventListener('change', (e) => {
            this.updateFormatDisplay(e.target.value);
            this.lastFormat = e.target.value;
        });

        // Copy to clipboard
        copyBtn.addEventListener('click', async () => {
            try {
                await navigator.clipboard.writeText(this.currentContent);
                this.showStatus(statusDiv, 'Copied to clipboard!', 'success');
                this.lastAction = 'copy';
            } catch (error) {
                console.error('ERROR: Failed to copy to clipboard:', error);
                this.showStatus(statusDiv, 'Failed to copy to clipboard', 'error');
            }
        });

        // Save to file
        saveBtn.addEventListener('click', () => {
            const filenameInput = this.getElement('_filename');
            const converter = this.getCurrentConverter();
            const filename = (filenameInput.value.trim() || this.options.defaultFilename) + converter.fileExtension;

            this.downloadFile(filename, this.currentContent, converter.mimeType || 'text/plain');
            this.showStatus(statusDiv, `Saved as "${filename}"`, 'success');
            this.lastAction = 'save';
            this.lastFilename = filename;
            this.lastFormat = this.currentFormat;
        });
    }


    /**
     * Returns the dialog result based on the last user action.
     * Includes the action type, last selected format, and filename if saved.
     * @returns {{action: string, format?: string, filename?: string}} Result object
     * @override
     */
    getDefaultResult() {
        const result = { action: this.lastAction || 'close' };
        if (this.lastFormat) result.format = this.lastFormat;
        if (this.lastFilename) result.filename = this.lastFilename;
        return result;
    }

    /**
     * Downloads content as a file.
     * @param {string} filename - The filename to save as
     * @param {string} content - The content to save
     * @param {string} mimeType - The MIME type for the file
     * @private
     */
    downloadFile(filename, content, mimeType) {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    /**
     * Shows a status message that fades out after a delay.
     * @param {HTMLElement} element - The status element
     * @param {string} message - The message to display
     * @param {string} type - 'success' or 'error'
     * @private
     */
    showStatus(element, message, type) {
        element.textContent = message;
        element.className = `export-dialog-status export-dialog-status-${type}`;

        // Clear after delay
        setTimeout(() => {
            element.textContent = '';
            element.className = 'export-dialog-status';
        }, 3000);
    }

    /**
     * Shows the export dialog (singleton - only one can be open at a time).
     * @param {Object} options - Configuration options (see constructor)
     * @returns {Promise<{action: string, format?: string, filename?: string}>} Action taken
     */
    static async showExportDialog(options = {}) {
        return BaseDialog.showSingleton(ExportDialog, options);
    }
}
